/**
 * MissingMaterialsWidget – Phase 4: Analytics & Library Integration
 * Toont top 10 vaak geïmporteerde Diversen-items die nog niet in de Global Material Library staan.
 * Per item: "Add to Library" opent Add Material modal met vooringevulde gegevens.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { FileText, Plus, RefreshCw, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../Toast';
import AddMaterialFromMissingModal from './AddMaterialFromMissingModal';

export interface MissingMaterialItem {
  description: string;
  count: number;
  avg_price: number;
  unit: string;
}

export interface MissingMaterialsReportResponse {
  items: MissingMaterialItem[];
}

export default function MissingMaterialsWidget() {
  const { t } = useTranslation();
  const toast = useToast();
  const [addModalItem, setAddModalItem] = useState<MissingMaterialItem | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['missing-materials-report'],
    queryFn: async () => {
      const response = await api.get<MissingMaterialsReportResponse>('/orders/missing-materials-report');
      return response.data;
    },
  });

  const items = data?.items ?? [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-500" aria-hidden />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('procurement.missingMaterials.title', { defaultValue: 'Vaak ontbrekende materialen' })}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-1.5 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded disabled:opacity-50"
          title={t('common.refresh', 'Ververs')}
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {t('procurement.missingMaterials.subtitle', {
            defaultValue: 'Items die vaak als Diversen uit PDF-import komen en nog niet in de bibliotheek staan.',
          })}
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('procurement.missingMaterials.empty', {
              defaultValue: 'Geen ontbrekende materialen gevonden. Importeer PDF-offertes om suggesties te zien.',
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="text-left py-2 px-2 font-medium text-gray-500 dark:text-gray-400">
                    {t('common.description', 'Beschrijving')}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-500 dark:text-gray-400">
                    {t('procurement.missingMaterials.count', { defaultValue: 'Aantal' })}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-500 dark:text-gray-400">
                    {t('procurement.missingMaterials.avgPrice', { defaultValue: 'Gem. prijs' })}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-500 dark:text-gray-400">
                    {t('procurement.missingMaterials.unit', { defaultValue: 'Eenheid' })}
                  </th>
                  <th className="text-right py-2 px-2 w-28" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={`${item.description}-${idx}`}
                    className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  >
                    <td className="py-2 px-2 text-gray-900 dark:text-white max-w-[200px] truncate" title={item.description}>
                      {item.description}
                    </td>
                    <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{item.count}</td>
                    <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">
                      € {item.avg_price.toFixed(2)}
                    </td>
                    <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{item.unit}</td>
                    <td className="py-2 px-2 text-right">
                      <button
                        type="button"
                        onClick={() => setAddModalItem(item)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {t('procurement.missingMaterials.addToLibrary', { defaultValue: 'Toevoegen aan bibliotheek' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addModalItem && (
        <AddMaterialFromMissingModal
          item={addModalItem}
          onClose={() => setAddModalItem(null)}
          onSuccess={() => {
            setAddModalItem(null);
            refetch();
            toast.success(t('procurement.missingMaterials.addedSuccess', { defaultValue: 'Materiaal toegevoegd aan bibliotheek' }));
          }}
        />
      )}
    </div>
  );
}
